import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Link2, Plus, X } from "lucide-react";
import ProductPicker from "./ProductPicker";

function LinkEditor({ base, onSaved }) {
  const [links, setLinks] = useState(null);
  useEffect(() => { api.get(`/dc-kits/accessory-links/${base.product_id}`).then(({ data }) => setLinks(data.accessories)).catch(() => setLinks([])); }, [base.product_id]);
  const save = async (next) => {
    try { const { data } = await api.put(`/dc-kits/accessory-links/${base.product_id}`, { product_ids: next.map((p) => p.id) }); setLinks(data.accessories); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save links"); }
  };
  if (!links) return null;
  return (
    <div className="mt-1.5 rounded border border-amber-200 bg-white p-2 space-y-1.5" data-testid={`dc-links-editor-${base.product_id}`}>
      <div className="text-[10px] text-slate-500">Linked accessories for <b>{base.product_name}</b> — always suggested first</div>
      <div className="flex flex-wrap gap-1">
        {links.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-2 pr-1 text-[11px]" data-testid={`dc-link-${p.id}`}>
            {p.product_name}<button type="button" onClick={() => save(links.filter((x) => x.id !== p.id))} aria-label="Unlink" data-testid={`dc-unlink-${p.id}`}><X className="h-3 w-3 text-slate-400 hover:text-slate-800" /></button>
          </span>
        ))}
        {!links.length && <span className="text-[11px] text-slate-400">No linked accessories yet</span>}
      </div>
      <ProductPicker onPick={(p) => !links.some((x) => x.id === p.id) && save([...links, p])} testId={`dc-link-pick-${base.product_id}`} placeholder="Link an accessory…" autoFocus={false} />
    </div>
  );
}

export default function AccessorySuggestions({ dc, reload }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/dc-kits/suggestions/${dc.id}`).then(({ data: d }) => setData(d)).catch(() => setData(null)), [dc.id]);
  useEffect(() => { load(); }, [load, dc.items?.length]);
  const add = async (p) => {
    setBusy(p.id);
    try { await api.post(`/delivery-challans/${dc.id}/extra-assets`, { product_id: p.id, qty: 1 }); toast.success(`${p.product_name} added to the DC`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(null); }
  };
  const bases = (data?.bases || []).filter((b) => b.suggestions.length || (data.can_edit_links && b.product_id));
  if (!bases.length) return null;
  return (
    <div className="w-full rounded-md border border-amber-200 bg-amber-50/50 p-2.5 space-y-2" data-testid="dc-accessory-suggestions">
      {bases.map((b) => (
        <div key={b.product_id || b.product_name}>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
            <span className="font-semibold text-amber-900">Complete the kit for {b.product_name}</span>
            {data.can_edit_links && b.product_id && (
              <button type="button" onClick={() => setEditing(editing === b.product_id ? null : b.product_id)} className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900" data-testid={`dc-links-manage-${b.product_id}`}>
                <Link2 className="h-3 w-3" />{editing === b.product_id ? "Done" : "Linked accessories"}
              </button>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {b.suggestions.map((p) => (
              <button key={p.id} type="button" disabled={busy === p.id} onClick={() => add(p)} title={p.reason} data-testid={`dc-suggest-${p.id}`}
                className="group inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] text-slate-800 hover:border-amber-500 hover:bg-amber-100 transition-colors disabled:opacity-50">
                <Plus className="h-3 w-3 shrink-0 text-amber-700" /><span className="min-w-0 max-w-[200px] truncate">{p.product_name}</span>
                <span className="hidden sm:inline shrink-0 text-[9px] text-slate-500">{p.reason}</span>
              </button>
            ))}
            {!b.suggestions.length && <span className="text-[11px] text-slate-500">No suggestions yet — link accessories to start</span>}
          </div>
          {editing === b.product_id && <LinkEditor base={b} onSaved={load} />}
        </div>
      ))}
    </div>
  );
}
